import * as React from "react"
import { useState, useEffect, useCallback, useRef } from "react"
import { motion, AnimatePresence } from "motion/react"
import { useAuth } from "@/contexts/AuthContext"
import { usePokerWebSocket } from "@/hooks/usePokerWebSocket"
import { useSound } from "@/hooks/useSound"
import type { Card, Phase, SidePot } from "@/lib/pokerEngine"
import type { PublicRoomState, PublicPlayerState, ShowdownResultPublic } from "@/lib/pokerBroadcast"
import {
  Plus,
  Users,
  ArrowLeft,
  X,
  Spade,
  Diamond,
  Heart,
  Club,
  Bot,
  Trash2,
} from "lucide-react"
import { ChatOverlay, RoomInfoOverlay, HandRankingsOverlay } from "./PokerOverlays"
import { ChipStack } from "@/components/poker/ChipStack"
import { FlyingChip, type FlyingChipData } from "@/components/poker/ChipAnimation"
import { useIsMobile } from "@/hooks/use-mobile"
import BetControls from "@/components/poker/BetControls"
import ActionButtons from "@/components/poker/ActionButtons"
import { DealingAnimation } from "@/components/poker/DealingAnimation"
import HandStrengthTip from "@/components/poker/HandStrengthTip"

// Sound assets (Kenney casino CC0 + OpenGameArt CC0 + SoundJay free)
import cardDealSfx from "@/public/sounds/poker-card-deal.wav"
import cardPlaceSfx from "@/public/sounds/poker-card-place.wav"
import chipBetSfx from "@/public/sounds/poker-chip-bet.wav"
import chipsCollodeSfx from "@/public/sounds/poker-chips-collide.wav"
import chipsStackSfx from "@/public/sounds/poker-chips-stack.wav"
import checkSfx from "@/public/sounds/poker-check.wav"
import foldSfx from "@/public/sounds/poker-fold.wav"
import allInSfx from "@/public/sounds/poker-all-in.wav"
import communitySfx from "@/public/sounds/poker-community.wav"
import showdownSfx from "@/public/sounds/poker-showdown.wav"
import shuffleSfx from "@/public/sounds/poker-shuffle.wav"
import winSfx from "@/public/sounds/poker-win.wav"
import loseSfx from "@/public/sounds/poker-lose.wav"
import yourTurnSfx from "@/public/sounds/poker-your-turn.wav"
import timerTickSfx from "@/public/sounds/poker-timer-tick.wav"
import gameStartSfx from "@/public/sounds/poker-game-start.wav"
import pokerLobbyBg from "@/public/poker/backgrounds/poker-lobby-bg.png"
import pokerTableRoomBg from "@/public/poker/backgrounds/poker-table-room-bg.png"

// ---- Card Component ----

function PokerCard({
  card,
  faceDown = false,
  size = "md",
  className = "",
  delay = 0,
  flipDelayMs = 0,
}: {
  card?: Card
  faceDown?: boolean
  size?: "sm" | "md" | "lg"
  className?: string
  delay?: number
  flipDelayMs?: number
}) {
  const [revealed, setRevealed] = useState(flipDelayMs <= 0)

  useEffect(() => {
    if (flipDelayMs > 0) {
      setRevealed(false)
      const t = setTimeout(() => setRevealed(true), flipDelayMs)
      return () => clearTimeout(t)
    }
  }, [flipDelayMs])

  const sizes = {
    sm: { cls: "w-[60px] h-[84px]" },
    md: { cls: "w-[80px] h-[112px]" },
    lg: { cls: "w-[100px] h-[140px]" },
  }
  const s = sizes[size]
  const showBack = faceDown || !card || !revealed

  const src = showBack
    ? "/cards/deck/card_back.svg"
    : `/cards/deck/${card.suit}_${card.value}.svg`

  // Two-phase animation: deal in face-down, then 3D Y-axis flip to reveal
  if (flipDelayMs > 0) {
    return (
      <motion.div
        className={`${s.cls} rounded-lg border-2 border-[#2A2A45]/40 overflow-hidden shadow-lg ${className}`}
        style={{ perspective: 800 }}
        initial={{ opacity: 0, y: -40, scale: 0.7 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ delay, duration: 0.35, type: "spring", stiffness: 300, damping: 20 }}
      >
        <motion.div
          className="w-full h-full relative"
          style={{ transformStyle: "preserve3d" }}
          animate={{ rotateY: revealed ? 180 : 0 }}
          transition={{ duration: 0.45, ease: [0.455, 0.03, 0.515, 0.955] }}
        >
          {/* Back face */}
          <img
            src="/cards/deck/card_back.svg"
            alt="Card back"
            className="w-full h-full object-cover absolute inset-0"
            style={{ backfaceVisibility: "hidden" }}
            draggable={false}
          />
          {/* Front face (pre-rotated 180deg so it shows when container flips) */}
          <img
            src={card ? `/cards/deck/${card.suit}_${card.value}.svg` : "/cards/deck/card_back.svg"}
            alt={card ? `${card.value} of ${card.suit}` : "Card"}
            className="w-full h-full object-cover absolute inset-0"
            style={{ backfaceVisibility: "hidden", transform: "rotateY(180deg)" }}
            draggable={false}
          />
        </motion.div>
      </motion.div>
    )
  }

  return (
    <motion.div
      className={`${s.cls} rounded-lg border-2 border-[#2A2A45]/40 overflow-hidden shadow-lg ${className}`}
      initial={{ opacity: 0, y: -30, rotateY: 180 }}
      animate={{ opacity: 1, y: 0, rotateY: 0 }}
      transition={{ delay, duration: 0.4, type: "spring", bounce: 0.3 }}
    >
      <img
        src={src}
        alt={card ? `${card.value} of ${card.suit}` : "Card back"}
        className="w-full h-full object-cover"
        draggable={false}
      />
    </motion.div>
  )
}

// ---- Suit Icon ----
function SuitIcon({ suit, className = "w-4 h-4" }: { suit: string; className?: string }) {
  switch (suit) {
    case "spades": return <Spade className={`${className} text-white`} />
    case "hearts": return <Heart className={`${className} text-red-500`} />
    case "diamonds": return <Diamond className={`${className} text-red-500`} />
    case "clubs": return <Club className={`${className} text-white`} />
    default: return <Spade className={className} />
  }
}

// ---- Create Room Modal ----

function CreateRoomModal({
  open,
  onClose,
  onCreate,
}: {
  open: boolean
  onClose: () => void
  onCreate: (name: string, settings: any) => void
}) {
  const [name, setName] = useState("")
  const [smallBlind, setSmallBlind] = useState(10)
  const [bigBlind, setBigBlind] = useState(20)
  const [buyIn, setBuyIn] = useState(200)
  const [maxPlayers, setMaxPlayers] = useState(6)
  const [loading, setLoading] = useState(false)

  const handleCreate = async () => {
    if (!name.trim()) return
    setLoading(true)
    await onCreate(name.trim(), {
      maxPlayers,
      smallBlind,
      bigBlind,
      minBuyIn: buyIn,
      maxBuyIn: buyIn * 5,
    })
    setLoading(false)
    setName("")
    onClose()
  }

  if (!open) return null

  return (
    <motion.div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <motion.div
        className="bg-[#1C1C32] border-4 border-[#2A2A45] rounded-3xl shadow-[8px_8px_0_0_#1a1a1a] p-6 w-full max-w-md"
        initial={{ scale: 0.8, y: 30 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.8, y: 30 }}
        transition={{ type: "spring", bounce: 0.3 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-5">
          <h2
            className="text-2xl font-black text-white"
            style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}
          >
            CREATE ROOM
          </h2>
          <button type="button" onClick={onClose} className="p-1.5 hover:bg-[#1A1A30] rounded-xl transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-xs font-bold text-[#6A6A8A] uppercase mb-1" style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}>
              Room Name
            </label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter room name..."
              className="w-full px-4 py-2.5 bg-[#1A1A30] border-3 border-[#2A2A45] rounded-xl font-bold text-sm text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-[#F37020]"
              style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}
              maxLength={30}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-bold text-[#6A6A8A] uppercase mb-1" style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}>
                Small Blind
              </label>
              <input
                type="number"
                value={smallBlind}
                onChange={(e) => {
                  const val = Math.max(1, parseInt(e.target.value) || 1)
                  setSmallBlind(val)
                  setBigBlind(val * 2)
                  setBuyIn(val * 20)
                }}
                className="w-full px-3 py-2.5 bg-[#1A1A30] border-3 border-[#2A2A45] rounded-xl font-bold text-sm text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-[#F37020]"
                style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}
                min={1}
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-[#6A6A8A] uppercase mb-1" style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}>
                Big Blind
              </label>
              <input
                type="number"
                value={bigBlind}
                onChange={(e) => setBigBlind(Math.max(2, parseInt(e.target.value) || 2))}
                className="w-full px-3 py-2.5 bg-[#1A1A30] border-3 border-[#2A2A45] rounded-xl font-bold text-sm text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-[#F37020]"
                style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}
                min={2}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-bold text-[#6A6A8A] uppercase mb-1" style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}>
                Buy-In (Points)
              </label>
              <input
                type="number"
                value={buyIn}
                onChange={(e) => setBuyIn(Math.max(10, parseInt(e.target.value) || 10))}
                className="w-full px-3 py-2.5 bg-[#1A1A30] border-3 border-[#2A2A45] rounded-xl font-bold text-sm text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-[#F37020]"
                style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}
                min={10}
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-[#6A6A8A] uppercase mb-1" style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}>
                Max Players
              </label>
              <select
                value={maxPlayers}
                onChange={(e) => setMaxPlayers(parseInt(e.target.value))}
                className="w-full px-3 py-2.5 bg-[#1A1A30] border-3 border-[#2A2A45] rounded-xl font-bold text-sm text-white focus:outline-none focus:ring-2 focus:ring-[#F37020]"
                style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}
              >
                {[2, 3, 4, 5, 6].map((n) => (
                  <option key={n} value={n}>{n} players</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <motion.button
          onClick={handleCreate}
          disabled={!name.trim() || loading}
          className="w-full mt-5 py-3 bg-[#F37020] text-white font-black text-lg border-3 border-[#2A2A45] rounded-xl shadow-[0_0_10px_rgba(243,112,32,0.3)] disabled:opacity-50 disabled:cursor-not-allowed"
          style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}
          whileHover={{ scale: 1.02, boxShadow: "5px 5px 0 0 #1a1a1a" }}
          whileTap={{ scale: 0.98, boxShadow: "2px 2px 0 0 #1a1a1a" }}
        >
          {loading ? "CREATING..." : `CREATE ROOM (${buyIn} PTS)`}
        </motion.button>
      </motion.div>
    </motion.div>
  )
}

// ---- Room Card ----

function RoomCard({
  room,
  onJoin,
}: {
  room: any
  onJoin: (roomId: string) => void
}) {
  return (
    <motion.div
      className="bg-[#1C1C32] border-3 border-[#2A2A45] rounded-2xl shadow-[0_0_10px_rgba(243,112,32,0.15)] overflow-hidden hover:shadow-[6px_6px_0_0_#1a1a1a] transition-shadow"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -2 }}
    >
      <div className="bg-gradient-to-r from-emerald-600 to-emerald-800 px-4 py-2.5 flex items-center justify-between">
        <h3
          className="text-white font-black text-lg truncate"
          style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}
        >
          {room.name}
        </h3>
        <div className="flex gap-1">
          {["spades", "hearts", "diamonds", "clubs"].map((s) => (
            <SuitIcon key={s} suit={s} className="w-3.5 h-3.5 text-white/60" />
          ))}
        </div>
      </div>
      <div className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-1.5">
            <Users className="w-4 h-4 text-[#6A6A8A]" />
            <span className="text-sm font-bold text-[#9A9ABF]" style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}>
              {room.playerCount}/{room.maxPlayers}
            </span>
          </div>
          <span
            className={`text-xs font-bold px-2.5 py-1 rounded-full border-2 ${
              room.status === "waiting"
                ? "bg-emerald-500/20 border-emerald-500/40 text-emerald-400"
                : "bg-[#F37020]/20 border-[#F37020]/40 text-[#F37020]"
            }`}
            style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}
          >
            {room.status === "waiting" ? "Waiting" : "Playing"}
          </span>
        </div>
        <div className="grid grid-cols-2 gap-2 mb-3 text-xs">
          <div className="bg-[#1A1A30] px-2.5 py-1.5 rounded-lg border border-[#2A2A45]">
            <span className="text-[#6A6A8A] font-bold uppercase" style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}>Blinds</span>
            <p className="font-black text-[#CCCCCC]" style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}>
              {room.smallBlind}/{room.bigBlind}
            </p>
          </div>
          <div className="bg-[#1A1A30] px-2.5 py-1.5 rounded-lg border border-[#2A2A45]">
            <span className="text-[#6A6A8A] font-bold uppercase" style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}>Buy-In</span>
            <p className="font-black text-[#CCCCCC]" style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}>
              {room.minBuyIn} pts
            </p>
          </div>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-xs text-[#6A6A8A] font-medium" style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}>
            by {room.creatorName}
          </span>
          <motion.button
            onClick={() => onJoin(room._id)}
            disabled={room.playerCount >= room.maxPlayers}
            className="px-4 py-1.5 bg-[#F37020] text-white font-bold text-sm border-2 border-[#2A2A45] rounded-xl shadow-[0_0_6px_rgba(243,112,32,0.1)] disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Join
          </motion.button>
        </div>
      </div>
    </motion.div>
  )
}

// ---- Player Seat ----

// Calculate visual seat position relative to current user's perspective
// Current user always appears at visual position 0 (bottom-center)
const getVisualSeatIndex = (actualSeatIndex: number, currentUserSeatIndex: number, maxSeats = 6): number => {
  return (actualSeatIndex - currentUserSeatIndex + maxSeats) % maxSeats
}

const SEAT_POSITIONS: Record<number, { top: string; left: string }> = {
  0: { top: "78%", left: "50%" },   // bottom center (current player)
  1: { top: "60%", left: "8%" },    // left bottom
  2: { top: "15%", left: "8%" },    // left top
  3: { top: "5%", left: "50%" },    // top center
  4: { top: "15%", left: "92%" },   // right top
  5: { top: "60%", left: "92%" },   // right bottom
}

function PlayerSeat({
  currentUserSeatIndex,
  player,
  seatIndex,
  isCurrentTurn,
  isDealer,
  isSmallBlind,
  isBigBlind,
  isCurrentUser,
  isWinner,
  turnTimeLeft,
}: {
  player?: PublicPlayerState
  seatIndex?: number
  isCurrentTurn: boolean
  isDealer: boolean
  isSmallBlind: boolean
  isBigBlind: boolean
  isCurrentUser: boolean
  isWinner: boolean
  turnTimeLeft: number
  currentUserSeatIndex: number
}) {
  const actualSeatIndex = player?.seatIndex ?? seatIndex ?? 0
  const visualSeatIndex = getVisualSeatIndex(actualSeatIndex, currentUserSeatIndex)
  const pos = SEAT_POSITIONS[visualSeatIndex] || { top: "50%", left: "50%" }
  const [avatarFailed, setAvatarFailed] = useState(false)
  const avatarSrc = player?.profilePicture || null
  const showAvatar = Boolean(player && avatarSrc && !avatarFailed)

  return (
    <motion.div
      className="absolute -translate-x-1/2 -translate-y-1/2"
      style={{ top: pos.top, left: pos.left }}
      initial={{ opacity: 0, scale: 0 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ type: "spring", bounce: 0.4 }}
    >
      {!player ? (
        <div className="w-16 h-16 rounded-full border-2 border-dashed border-white/20 flex items-center justify-center">
          <span className="text-white/30 text-xs font-bold" style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}>
            Empty
          </span>
        </div>
      ) : (
        <div className="flex flex-col items-center gap-1">
          {/* Avatar with timer ring */}
          <div className="relative">
            {isWinner && (
              <motion.div
                className="absolute -inset-2 rounded-full border-2 border-[#FFD700]"
                animate={{ scale: [1, 1.15, 1], opacity: [0.8, 0.3, 0.8] }}
                transition={{ duration: 1.2, repeat: Infinity, ease: "easeInOut" }}
              />
            )}
            {isCurrentTurn && !isWinner && (
              <svg className="absolute -inset-1 w-[calc(100%+8px)] h-[calc(100%+8px)]" viewBox="0 0 40 40">
                <circle
                  cx="20" cy="20" r="18"
                  fill="none"
                  stroke="#FFD700"
                  strokeWidth="2.5"
                  strokeDasharray={`${(turnTimeLeft / 30) * 113} 113`}
                  strokeLinecap="round"
                  transform="rotate(-90 20 20)"
                  className="transition-all duration-1000"
                />
              </svg>
            )}
            <div
              className={`w-12 h-12 rounded-full flex items-center justify-center border-3 font-black text-lg text-white overflow-hidden
                ${isWinner ? "border-[#FFD700] shadow-[0_0_20px_rgba(255,215,0,0.7),0_0_40px_rgba(255,215,0,0.3)]" : isCurrentTurn ? "border-[#FFD700] shadow-[0_0_12px_rgba(255,215,0,0.5)]" : "border-white/50"}
                ${player.folded && !isWinner ? "opacity-40" : ""}
                ${isCurrentUser ? "bg-gradient-to-br from-[#F37020] to-[#FF6B00]" : "bg-gradient-to-br from-purple-500 to-purple-700"}
              `}
              style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}
            >
              {showAvatar ? (
                <img
                  src={avatarSrc!}
                  alt={`${player.displayName} avatar`}
                  className="w-full h-full object-cover"
                  draggable={false}
                  onError={() => setAvatarFailed(true)}
                />
              ) : (
                player.displayName.charAt(0).toUpperCase()
              )}
            </div>
            {isDealer && (
              <div className="absolute -top-1 -right-1 w-5 h-5 bg-[#FFD700] rounded-full border-2 border-[#2A2A45] flex items-center justify-center text-[10px] font-black text-[#2A2A45]">
                D
              </div>
            )}
            {isSmallBlind && !isDealer && (
              <div className="absolute -top-1 -right-1 w-5 h-5 bg-[#3B82F6] rounded-full border-2 border-[#2A2A45] flex items-center justify-center text-[8px] font-black text-white">
                SB
              </div>
            )}
            {isBigBlind && (
              <div className={`absolute ${isDealer ? "-top-1 -left-1" : "-bottom-1 -right-1"} w-5 h-5 bg-[#EF4444] rounded-full border-2 border-[#2A2A45] flex items-center justify-center text-[8px] font-black text-white`}>
                BB
              </div>
            )}
            {player?.isBot && (
              <div className="absolute -bottom-1 -left-1 w-5 h-5 bg-[#9B59B6] rounded-full border-2 border-[#2A2A45] flex items-center justify-center">
                <Bot className="w-3 h-3 text-white" />
              </div>
            )}
          </div>
          {/* Name & Chips */}
          <div className={`text-center ${player.folded ? "opacity-40" : ""}`}>
            <p
              className="text-white text-xs font-bold truncate max-w-[80px]"
              style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}
            >
              {isCurrentUser ? "You" : player.displayName}{player?.isBot ? " (Bot)" : ""}
            </p>
            <p
              className="text-[#FFD700] text-xs font-black"
              style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}
            >
              {player.chips.toLocaleString()}
            </p>
          </div>
          {/* Visual Chip Stack */}
          <div className={`flex justify-center ${player.folded ? "opacity-40" : ""}`}>
            <ChipStack amount={player.chips} size={16} compact className="md:hidden" />
            <ChipStack amount={player.chips} size={20} compact className="hidden md:block" />
          </div>
          {/* Bet amount */}
          {player.bet > 0 && (
            <motion.div
              className="bg-black/60 px-2 py-0.5 rounded-full border border-[#FFD700]/50"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
            >
              <span className="text-[#FFD700] text-[10px] font-bold" style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}>
                {player.bet.toLocaleString()}
              </span>
            </motion.div>
          )}
        </div>
      )}
    </motion.div>
  )
}

// ---- Blind Position Helpers ----

function getBlindSeatIndices(players: PublicPlayerState[], dealerIndex: number): { sbSeatIndex: number; bbSeatIndex: number } {
  // Find active (non-zero-chip) players sorted by seat index
  const activeSeatIndices = players
    .filter(p => p.chips > 0 || p.bet > 0)
    .map(p => p.seatIndex)
    .sort((a, b) => a - b)
  if (activeSeatIndices.length < 2) return { sbSeatIndex: -1, bbSeatIndex: -1 }

  // Find the dealer's position in the sorted active seats
  const dealerPos = activeSeatIndices.indexOf(dealerIndex)
  if (dealerPos === -1) {
    // Dealer not in active seats — use first two active seats
    return { sbSeatIndex: activeSeatIndices[0]!, bbSeatIndex: activeSeatIndices[1]! }
  }

  // SB = next active seat after dealer, BB = next active seat after SB
  const sbPos = (dealerPos + 1) % activeSeatIndices.length
  const bbPos = (sbPos + 1) % activeSeatIndices.length
  // In heads-up (2 players), dealer is SB
  if (activeSeatIndices.length === 2) {
    return { sbSeatIndex: activeSeatIndices[dealerPos]!, bbSeatIndex: activeSeatIndices[sbPos]! }
  }
  return { sbSeatIndex: activeSeatIndices[sbPos]!, bbSeatIndex: activeSeatIndices[bbPos]! }
}

// ---- Poker Table (game view) ----

function PokerTable({
  roomId,
  roomName,
  onLeave,
}: {
  roomId: string
  roomName: string
  onLeave: () => void
}) {
  const { user, token, addPokerBot, removePokerBot } = useAuth()
  const userId = user?._id || ""
  const isMobile = useIsMobile()

  // Sound effects
  const { play: playCardDeal } = useSound(cardDealSfx, { volume: 0.5 })
  const { play: playCardPlace } = useSound(cardPlaceSfx, { volume: 0.5 })
  const { play: playChipBet } = useSound(chipBetSfx, { volume: 0.6 })
  const { play: playChipsCollide } = useSound(chipsCollodeSfx, { volume: 0.5 })
  const { play: playChipsStack } = useSound(chipsStackSfx, { volume: 0.5 })
  const { play: playCheck } = useSound(checkSfx, { volume: 0.5 })
  const { play: playFold } = useSound(foldSfx, { volume: 0.4 })
  const { play: playAllIn } = useSound(allInSfx, { volume: 0.7 })
  const { play: playCommunity } = useSound(communitySfx, { volume: 0.5 })
  const { play: playShowdown } = useSound(showdownSfx, { volume: 0.6 })
  const { play: playShuffle } = useSound(shuffleSfx, { volume: 0.5 })
  const { play: playWin } = useSound(winSfx, { volume: 0.7 })
  const { play: playLose } = useSound(loseSfx, { volume: 0.5 })
  const { play: playYourTurn } = useSound(yourTurnSfx, { volume: 0.6 })
  const { play: playTimerTick } = useSound(timerTickSfx, { volume: 0.3 })
  const { play: playGameStart } = useSound(gameStartSfx, { volume: 0.6 })

  const [roomState, setRoomState] = useState<PublicRoomState | null>(null)
  const [showBotMenu, setShowBotMenu] = useState(false)
  const [addingBot, setAddingBot] = useState(false)
  const [holeCards, setHoleCards] = useState<Card[]>([])
  const [currentTurnUserId, setCurrentTurnUserId] = useState<string | null>(null)
  const [turnTimeLeft, setTurnTimeLeft] = useState(30)
  const [turnTimerDuration, setTurnTimerDuration] = useState(30)
  const [raiseAmount, setRaiseAmount] = useState(0)
  const [showdownResults, setShowdownResults] = useState<ShowdownResultPublic[] | null>(null)
  const [winners, setWinners] = useState<{ userId: string; amount: number }[] | null>(null)
  const [autoStartCountdown, setAutoStartCountdown] = useState<number | null>(null)
  const [actionLog, setActionLog] = useState<string[]>([])
  const [chatMessages, setChatMessages] = useState<{ playerName: string; text: string; timestamp: number }[]>([])
  const [flyingChips, setFlyingChips] = useState<FlyingChipData[]>([])
  const [dealingActive, setDealingActive] = useState(false)
  const [holeCardsJustDealt, setHoleCardsJustDealt] = useState(false)
  const [communityFlipPhase, setCommunityFlipPhase] = useState<Phase | null>(null)
  const [winnerBanner, setWinnerBanner] = useState<{ name: string; amount: number } | null>(null)
  const [winChipAnims, setWinChipAnims] = useState<FlyingChipData[]>([])
  const turnTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const autoStartTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const addLog = useCallback((msg: string) => {
    setActionLog((prev) => [...prev.slice(-20), msg])
  }, [])

  const isCreator = roomState?.creatorId === userId
  const roomIsFull = roomState ? roomState.players.length >= 6 : true
  const botCount = roomState?.players.filter((p: any) => p.isBot).length || 0
  const canAddBot = isCreator && !roomIsFull && roomState?.phase === "waiting"

  const handleAddBot = useCallback(async (difficulty: "easy" | "medium" | "hard") => {
    setAddingBot(true)
    setShowBotMenu(false)
    const result = await addPokerBot(roomId, difficulty)
    if (result.success && result.bot) {
      addLog(`Bot "${result.bot.displayName}" joined (${difficulty})`)
    } else {
      addLog(`Failed to add bot: ${result.message}`)
    }
    setAddingBot(false)
  }, [roomId, addPokerBot, addLog])

  const handleRemoveBot = useCallback(async (seatIndex: number) => {
    const result = await removePokerBot(roomId, seatIndex)
    if (result.success) {
      addLog("Bot removed")
    } else {
      addLog(`Failed to remove bot: ${result.message}`)
    }
  }, [roomId, removePokerBot, addLog])


  const { sendAction, sendChat } = usePokerWebSocket({
    token,
    roomId,
    onRoomUpdate: useCallback((room: PublicRoomState) => {
      setRoomState(room)
      // Preserve showdown UI while the table is in showdown so the result
      // isn't immediately cleared by the follow-up room update broadcast.
      if (room.phase !== "showdown") {
        setShowdownResults(null)
        setWinners(null)
      } else {
        setCurrentTurnUserId(null)
        if (turnTimerRef.current) {
          clearInterval(turnTimerRef.current)
          turnTimerRef.current = null
        }
      }
      if (room.players.length < 2) {
        if (autoStartTimerRef.current) {
          clearInterval(autoStartTimerRef.current)
          autoStartTimerRef.current = null
        }
        setAutoStartCountdown(null)
      }
    }, []),
    onAutoStartCountdown: useCallback((seconds: number) => {
      const startSeconds = Math.max(1, Math.floor(seconds))
      if (autoStartTimerRef.current) {
        clearInterval(autoStartTimerRef.current)
        autoStartTimerRef.current = null
      }
      setAutoStartCountdown(startSeconds)
      addLog(`Game ready. Starting in ${startSeconds}s`)

      let remaining = startSeconds
      autoStartTimerRef.current = setInterval(() => {
        remaining -= 1
        if (remaining <= 0) {
          if (autoStartTimerRef.current) {
            clearInterval(autoStartTimerRef.current)
            autoStartTimerRef.current = null
          }
          setAutoStartCountdown(0)
          return
        }
        setAutoStartCountdown(remaining)
      }, 1000)
    }, [addLog]),
    onAutoStartCancelled: useCallback(() => {
      if (autoStartTimerRef.current) {
        clearInterval(autoStartTimerRef.current)
        autoStartTimerRef.current = null
      }
      setAutoStartCountdown(null)
      addLog("Countdown cancelled. Waiting for more players.")
    }, [addLog]),
    onGameStart: useCallback((handNumber: number) => {
      setHoleCards([])
      setShowdownResults(null)
      setWinners(null)
      setCommunityFlipPhase(null)
      setWinnerBanner(null)
      setWinChipAnims([])
      if (autoStartTimerRef.current) {
        clearInterval(autoStartTimerRef.current)
        autoStartTimerRef.current = null
      }
      setAutoStartCountdown(null)
      playShuffle()
      playGameStart()
      setDealingActive(true)
      addLog(`Hand #${handNumber} started`)
    }, [addLog, playShuffle, playGameStart]),
    onDealHoleCards: useCallback((cards: Card[]) => {
      setHoleCards(cards)
      setHoleCardsJustDealt(true)
      playCardDeal()
      setTimeout(() => playCardDeal(), 150)
      // Reset just-dealt flag after flip animations complete
      setTimeout(() => setHoleCardsJustDealt(false), 1500)
    }, [playCardDeal]),
    onPlayerAction: useCallback((uid: string, action: string, amount: number) => {
      const name = roomState?.players.find((p) => p.userId === uid)?.displayName || "Player"
      const amountStr = amount > 0 ? ` (${amount})` : ""
      // Play action-specific sound
      if (action === "fold") playFold()
      else if (action === "check") playCheck()
      else if (action === "call") playChipBet()
      else if (action === "raise") playChipsStack()
      else if (action === "all-in") playAllIn()
      addLog(`${name}: ${action}${amountStr}`)
      // Trigger chip animation for money actions
      if (amount > 0 && action !== "fold" && action !== "check") {
        const player = roomState?.players.find((p) => p.userId === uid)
        if (player) {
          const myP = roomState?.players.find((p) => p.userId === userId)
          const userSeat = myP?.seatIndex ?? 0
          const visualSeat = getVisualSeatIndex(player.seatIndex, userSeat)
          const pos = SEAT_POSITIONS[visualSeat]
          if (pos) {
            const bb = roomState?.bigBlind || 10
            const chipCount = Math.min(3, Math.max(1, Math.ceil(amount / bb / 2)))
            const chips: FlyingChipData[] = Array.from({ length: chipCount }, (_, i) => ({
              id: `${Date.now()}-${uid}-${i}`,
              fromTop: pos.top,
              fromLeft: pos.left,
              amount: Math.round(amount / chipCount),
              delay: i * 0.1,
            }))
            setFlyingChips((prev) => [...prev, ...chips])
          }
        }
      }
    }, [roomState, userId, addLog, playFold, playCheck, playChipBet, playChipsStack, playAllIn]),
    onCommunityCards: useCallback((_cards: Card[], phase: Phase) => {
      playCommunity()
      addLog(`${phase.charAt(0).toUpperCase() + phase.slice(1)} dealt`)
      setCommunityFlipPhase(phase)
      const resetDelay = phase === "flop" ? 1200 : 800
      setTimeout(() => setCommunityFlipPhase(null), resetDelay)
    }, [addLog, playCommunity]),
    onShowdown: useCallback((results: ShowdownResultPublic[]) => {
      setCurrentTurnUserId(null)
      if (turnTimerRef.current) {
        clearInterval(turnTimerRef.current)
        turnTimerRef.current = null
      }
      playShowdown()
      setShowdownResults(results)
    }, [playShowdown]),
    onPotUpdate: useCallback(() => {}, []),
    onTurnChange: useCallback((uid: string, timeLimit: number) => {
      setCurrentTurnUserId(uid)
      setTurnTimeLeft(timeLimit)
      setTurnTimerDuration(timeLimit)
      // Play your-turn notification if it's the current user
      if (uid === userId) playYourTurn()
      else playCardPlace()
      // Start countdown
      if (turnTimerRef.current) clearInterval(turnTimerRef.current)
      let remaining = timeLimit
      turnTimerRef.current = setInterval(() => {
        remaining--
        setTurnTimeLeft(remaining)
        // Tick sound in the last 5 seconds of your turn
        if (remaining <= 5 && remaining > 0 && uid === userId) playTimerTick()
        if (remaining <= 0 && turnTimerRef.current) {
          clearInterval(turnTimerRef.current)
        }
      }, 1000)
    }, [userId, playYourTurn, playCardPlace, playTimerTick]),
    onGameEnd: useCallback((w: { userId: string; amount: number }[]) => {
      setCurrentTurnUserId(null)
      if (turnTimerRef.current) {
        clearInterval(turnTimerRef.current)
        turnTimerRef.current = null
      }
      setWinners(w)
      playChipsCollide()
      // Win or lose sound based on whether current user is a winner
      const userWon = w.some((winner) => winner.userId === userId)
      if (userWon) setTimeout(() => playWin(), 300)
      else setTimeout(() => playLose(), 300)
      const winnerNames = w.map((winner) => {
        const name = roomState?.players.find((p) => p.userId === winner.userId)?.displayName || "Player"
        return `${name} won ${winner.amount}`
      })
      addLog(`Winners: ${winnerNames.join(", ")}`)
      // Winner banner
      if (w.length > 0) {
        const names = w.map(wi => roomState?.players.find(p => p.userId === wi.userId)?.displayName || "Player")
        const totalWon = w.reduce((sum, wi) => sum + wi.amount, 0)
        setWinnerBanner({ name: names.join(" & "), amount: totalWon })
        setTimeout(() => setWinnerBanner(null), 3000)
      }
      // Chip animation from pot to winner seat(s)
      const myP = roomState?.players.find((p) => p.userId === userId)
      const userSeat = myP?.seatIndex ?? 0
      const newWinChips: FlyingChipData[] = []
      w.forEach((winner, wIdx) => {
        const wp = roomState?.players.find((p) => p.userId === winner.userId)
        if (wp) {
          const visSeat = getVisualSeatIndex(wp.seatIndex, userSeat)
          const seatPos = SEAT_POSITIONS[visSeat]
          if (seatPos) {
            const bb = roomState?.bigBlind || 10
            const chipCount = Math.min(3, Math.max(1, Math.ceil(winner.amount / bb / 3)))
            for (let i = 0; i < chipCount; i++) {
              newWinChips.push({
                id: `win-${Date.now()}-${winner.userId}-${i}`,
                fromTop: seatPos.top,
                fromLeft: seatPos.left,
                amount: Math.round(winner.amount / chipCount),
                delay: wIdx * 0.15 + i * 0.1,
              })
            }
          }
        }
      })
      if (newWinChips.length > 0) setWinChipAnims(newWinChips)
    }, [roomState, addLog, userId, playChipsCollide, playWin, playLose]),
    onError: useCallback((msg: string) => { addLog(`Error: ${msg}`) }, [addLog]),
    onChat: useCallback((uid: string, playerName: string, text: string, ts: number) => {
      if (uid === userId) return
      setChatMessages((prev) => [...prev.slice(-50), { playerName, text, timestamp: ts }])
    }, [userId]),
    onReconnect: useCallback(() => { addLog("Reconnecting...") }, [addLog]),
  })

  useEffect(() => {
    return () => {
      if (turnTimerRef.current) clearInterval(turnTimerRef.current)
      if (autoStartTimerRef.current) clearInterval(autoStartTimerRef.current)
    }
  }, [])

  const isMyTurn = currentTurnUserId === userId
  const myPlayer = roomState?.players.find((p) => p.userId === userId)
  const currentUserSeatIndex = myPlayer?.seatIndex ?? 0
  const dealSeatPositions = React.useMemo(() => {
    if (!roomState) return []
    return roomState.players.map((p) => {
      const vis = getVisualSeatIndex(p.seatIndex, currentUserSeatIndex)
      return SEAT_POSITIONS[vis] || { top: "50%", left: "50%" }
    })
  }, [roomState?.players.length, currentUserSeatIndex])
  const canCheck = myPlayer && roomState && roomState.currentBet <= (myPlayer.bet || 0)
  const callAmount = myPlayer && roomState ? Math.min(roomState.currentBet - (myPlayer.bet || 0), myPlayer.chips) : 0
  const minRaise = roomState ? roomState.currentBet + roomState.bigBlind : 0

  useEffect(() => {
    if (roomState) {
      setRaiseAmount(minRaise)
    }
  }, [roomState?.currentBet, minRaise])

  return (
    <div className="h-full flex flex-col bg-gradient-to-b from-[#121220] to-[#0E0E1A] relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <img src={pokerTableRoomBg} alt="" aria-hidden="true" className="w-full h-full object-cover opacity-70" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#04070B]/55 via-[#04070B]/22 to-[#04070B]/70" />
      </div>
      {/* Header */}
      <div className="relative z-10 flex items-center justify-between px-4 py-3 bg-black/40 border-b border-white/10">
        <motion.button
          onClick={onLeave}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-red-500/20 text-red-400 rounded-xl border border-red-500/30 text-sm font-bold"
          style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <ArrowLeft className="w-4 h-4" />
          Leave
        </motion.button>
        <h2
          className="text-white font-black text-lg"
          style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}
        >
          {roomName}
        </h2>
        <div className="text-right">
          <span className="text-white/50 text-xs" style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}>
            Blinds {roomState?.smallBlind}/{roomState?.bigBlind}
          </span>
        </div>
      </div>

      {/* Table Area */}
      <div className="relative z-10 flex-1 overflow-hidden">
        {/* Felt Table */}
        <div className="absolute inset-4 md:inset-8 lg:inset-12">
          <div className="relative w-full h-full rounded-[50%] bg-gradient-to-b from-emerald-700 to-emerald-900 border-[6px] border-[#8B6914] shadow-[inset_0_0_80px_rgba(0,0,0,0.3),0_0_40px_rgba(0,0,0,0.5)]">
            {/* Inner felt ring */}
            <div className="absolute inset-3 rounded-[50%] border-2 border-emerald-500/20" />

            {/* Pot Display */}
            {roomState && roomState.pot > 0 && (
              <motion.div
                className="absolute top-[35%] left-1/2 -translate-x-1/2 -translate-y-1/2 z-10"
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ type: "spring", bounce: 0.3 }}
              >
                <div className="flex flex-col items-center gap-1">
                  {/* Phase Badge */}
                  {roomState.phase !== "waiting" && roomState.phase !== "showdown" && (
                    <motion.div
                      className="bg-black/60 px-2.5 py-0.5 rounded-full border border-white/20 backdrop-blur-sm"
                      key={roomState.phase}
                      initial={{ opacity: 0, y: -4 }}
                      animate={{ opacity: 1, y: 0 }}
                    >
                      <span
                        className="text-white/70 text-[10px] font-bold uppercase tracking-wider"
                        style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}
                      >
                        {roomState.phase === "preflop" ? "Pre-Flop" : roomState.phase.charAt(0).toUpperCase() + roomState.phase.slice(1)}
                      </span>
                    </motion.div>
                  )}
                  {/* Chip Stack (desktop only) */}
                  <div className="hidden md:flex justify-center">
                    <ChipStack amount={roomState.pot} size={20} compact animate />
                  </div>
                  {/* Pot Total */}
                  <motion.div
                    className="bg-black/60 px-4 py-1.5 rounded-full border border-[#FFD700]/40 backdrop-blur-sm shadow-[0_0_15px_rgba(255,215,0,0.15)]"
                    key={roomState.pot}
                    initial={{ scale: 1.1 }}
                    animate={{ scale: 1 }}
                    transition={{ duration: 0.2 }}
                  >
                    <span
                      className="text-[#FFD700] font-black text-sm md:text-lg"
                      style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}
                    >
                      POT: {roomState.pot.toLocaleString()}
                    </span>
                  </motion.div>
                  {/* Side Pots */}
                  {roomState.sidePots && roomState.sidePots.length > 1 && (
                    <div className="flex flex-wrap justify-center gap-1 mt-0.5">
                      {roomState.sidePots.map((sp, i) => (
                        <motion.div
                          key={i}
                          className="bg-black/50 px-2 py-0.5 rounded-full border border-white/20 backdrop-blur-sm"
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          transition={{ delay: i * 0.1 }}
                        >
                          <span
                            className="text-white/80 text-[10px] font-bold"
                            style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}
                          >
                            Side {i + 1}: {sp.amount.toLocaleString()}
                          </span>
                        </motion.div>
                      ))}
                    </div>
                  )}
                </div>
              </motion.div>
            )}

            {autoStartCountdown != null && roomState && roomState.players.length >= 2 && (
              <motion.div
                className="absolute top-[20%] left-1/2 -translate-x-1/2 z-20"
                initial={{ opacity: 0, y: -8, scale: 0.96 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0 }}
              >
                <div className="px-4 py-2 rounded-2xl border-2 border-[#FFD700]/50 bg-black/60 backdrop-blur-sm shadow-[0_8px_25px_rgba(0,0,0,0.35)]">
                  <p
                    className="text-[#FFD700] text-[11px] font-black text-center tracking-wider"
                    style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}
                  >
                    GAME READY
                  </p>
                  <p
                    className="text-white text-lg font-black text-center leading-tight"
                    style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}
                  >
                    {autoStartCountdown > 0 ? `Starting in ${autoStartCountdown}s` : "Starting..."}
                  </p>
                </div>
              </motion.div>
            )}

            {/* Flying chip animations */}
            <AnimatePresence>
              {flyingChips.map((chip) => (
                <FlyingChip
                  key={chip.id}
                  fromTop={chip.fromTop}
                  fromLeft={chip.fromLeft}
                  amount={chip.amount}
                  delay={chip.delay}
                  duration={isMobile ? 0.3 : 0.5}
                  onComplete={() => setFlyingChips((prev) => prev.filter((c) => c.id !== chip.id))}
                />
              ))}
            </AnimatePresence>
            {/* Winner chip animations (pot to winner seat) */}
            <AnimatePresence>
              {winChipAnims.map((chip) => (
                <motion.div
                  key={chip.id}
                  className="absolute z-30 w-5 h-5 rounded-full border-2 border-[#FFD700] shadow-[0_0_8px_rgba(255,215,0,0.6)]"
                  style={{
                    background: chip.amount >= 500 ? "#9B59B6" : chip.amount >= 100 ? "#1A1A1A" : chip.amount >= 25 ? "#22C55E" : chip.amount >= 10 ? "#3B82F6" : chip.amount >= 5 ? "#EF4444" : "#F5F5F5",
                  }}
                  initial={{ top: "35%", left: "50%", scale: 0.8, opacity: 1, x: "-50%", y: "-50%" }}
                  animate={{ top: chip.fromTop, left: chip.fromLeft, scale: 0.5, opacity: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ delay: chip.delay, duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }}
                  onAnimationComplete={() => setWinChipAnims((prev) => prev.filter((c) => c.id !== chip.id))}
                />
              ))}
            </AnimatePresence>

            {/* Dealing Animation */}
            {dealingActive && dealSeatPositions.length > 0 && (
              <DealingAnimation
                seatPositions={dealSeatPositions}
                staggerMs={isMobile ? 50 : 100}
                onComplete={() => setDealingActive(false)}
              />
            )}

            {/* Community Cards */}
            <div className="absolute top-[45%] left-1/2 -translate-x-1/2 -translate-y-1/2 flex gap-1.5 z-10">
              {roomState?.communityCards.map((card, i) => {
                // Calculate flip delay based on which phase just dealt
                let flipMs = 0
                const stagger = isMobile ? 150 : 200
                const base = isMobile ? 200 : 300
                if (communityFlipPhase === "flop" && i < 3) {
                  flipMs = base + i * stagger
                } else if (communityFlipPhase === "turn" && i === 3) {
                  flipMs = base
                } else if (communityFlipPhase === "river" && i === 4) {
                  flipMs = base
                }
                return (
                  <PokerCard
                    key={`${card.suit}_${card.value}`}
                    card={card}
                    size="sm"
                    delay={i * 0.15}
                    flipDelayMs={flipMs}
                  />
                )
              })}
              {/* Placeholders for undealt community cards */}
              {Array.from({ length: 5 - (roomState?.communityCards.length || 0) }).map((_, i) => (
                <div
                  key={`empty-${i}`}
                  className="w-[60px] h-[84px] rounded-lg border-2 border-dashed border-white/10"
                />
              ))}
            </div>

            {/* Player Seats */}
            {(() => {
              const blinds = roomState ? getBlindSeatIndices(roomState.players, roomState.dealerIndex) : { sbSeatIndex: -1, bbSeatIndex: -1 }
              return roomState?.players.map((player) => (
                <div key={player.userId} className="contents" onClick={() => {
                  if (player.isBot && isCreator && roomState?.phase === "waiting") handleRemoveBot(player.seatIndex)
                }} style={{ cursor: player.isBot && isCreator && roomState?.phase === "waiting" ? "pointer" : "default" }}>
                <PlayerSeat
                  player={player}
                  isCurrentTurn={currentTurnUserId === player.userId}
                  isDealer={roomState.dealerIndex === player.seatIndex}
                  isSmallBlind={player.seatIndex === blinds.sbSeatIndex}
                  isBigBlind={player.seatIndex === blinds.bbSeatIndex}
                  isCurrentUser={player.userId === userId}
                  isWinner={!!winners?.some((w) => w.userId === player.userId)}
                  turnTimeLeft={currentTurnUserId === player.userId ? turnTimeLeft : 30}
                  currentUserSeatIndex={currentUserSeatIndex}
                />
                </div>
              ))
            })()}
            {/* Empty seats */}
            {(() => {
              const maxSeats = 6
              const players = roomState?.players || []
              const takenSeats = new Set(players.map((p) => p.seatIndex))
              const emptySeats: number[] = []
              for (let s = 0; s < maxSeats; s++) {
                if (!takenSeats.has(s)) emptySeats.push(s)
              }
              return emptySeats.map((seatIdx) => (
                <PlayerSeat
                  key={`empty-seat-${seatIdx}`}
                  seatIndex={seatIdx}
                  isCurrentTurn={false}
                  isDealer={false}
                  isSmallBlind={false}
                  isBigBlind={false}
                  isCurrentUser={false}
                  isWinner={false}
                  currentUserSeatIndex={currentUserSeatIndex}
                  turnTimeLeft={30}
                />
              ))
            })()}
          </div>
        </div>

        {/* Hand Strength Tip Panel */}
        <AnimatePresence>
          {holeCards.length > 0 && (
            <HandStrengthTip
              holeCards={holeCards}
              communityCards={roomState?.communityCards || []}
            />
          )}
        </AnimatePresence>

        {/* Poker Overlays */}
        <RoomInfoOverlay
          roomName={roomName}
          smallBlind={roomState?.smallBlind || 0}
          bigBlind={roomState?.bigBlind || 0}
          playerCount={roomState?.players.length || 0}
          maxPlayers={6}
          pot={roomState?.pot || 0}
          phase={roomState?.phase || "waiting"}
        />
        <HandRankingsOverlay />
        <ChatOverlay actionLog={actionLog} roomName={roomName} onSendChat={sendChat} incomingChats={chatMessages} />
        {/* Winner Banner */}
        <AnimatePresence>
          {winnerBanner && (
            <motion.div
              className="absolute top-[12%] left-1/2 -translate-x-1/2 z-30 pointer-events-none"
              initial={{ opacity: 0, y: 20, scale: 0.8 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -10, scale: 0.9 }}
              transition={{ type: "spring", bounce: 0.4, duration: 0.5 }}
            >
              <div className="px-6 py-3 rounded-2xl bg-gradient-to-r from-[#FFD700]/20 via-[#FFD700]/30 to-[#FFD700]/20 border-2 border-[#FFD700] backdrop-blur-md shadow-[0_0_30px_rgba(255,215,0,0.3),0_0_60px_rgba(255,215,0,0.1)]">
                <p
                  className="text-[#FFD700] font-black text-lg md:text-xl text-center whitespace-nowrap"
                  style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}
                >
                  {winnerBanner.name} wins {winnerBanner.amount.toLocaleString()}!
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Showdown Results Overlay */}
        <AnimatePresence>
          {showdownResults && (
            <motion.div
              className="absolute inset-0 z-20 flex items-center justify-center bg-black/40 backdrop-blur-sm"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <motion.div
                className="bg-[#1C1C32]/95 border-2 border-[#FFD700] rounded-2xl p-5 max-w-sm w-full mx-4"
                initial={{ scale: 0.8 }}
                animate={{ scale: 1 }}
              >
                <h3 className="text-[#FFD700] font-black text-xl text-center mb-3" style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}>
                  SHOWDOWN
                </h3>
                <div className="space-y-3">
                  {showdownResults.map((result) => (
                    <div key={result.userId} className={`flex items-center gap-3 rounded-xl px-2 py-1.5 ${result.won > 0 ? "bg-[#FFD700]/10 border border-[#FFD700]/30" : ""}`}>
                      <div className="flex gap-1">
                        {result.holeCards.map((c) => (
                          <PokerCard key={`${c.suit}_${c.value}`} card={c} size="sm" />
                        ))}
                      </div>
                      <div className="flex-1">
                        <p className="text-white text-sm font-bold" style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}>
                          {roomState?.players.find((p) => p.userId === result.userId)?.displayName}
                        </p>
                        <p className="text-[#6A6A8A] text-xs">{result.bestHand.name}</p>
                      </div>
                      {result.won > 0 && (
                        <span className="text-[#32CD32] font-black text-sm" style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}>
                          +{result.won}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
                {winners && (
                  <div className="mt-3 pt-3 border-t border-white/10 text-center">
                    <p className="text-[#FFD700] font-bold text-sm" style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}>
                      {winners.map((w) => roomState?.players.find((p) => p.userId === w.userId)?.displayName || "").join(" & ")} WINS!
                    </p>
                  </div>
                )}
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Hole Cards Display */}
      {holeCards.length > 0 && (
        <div className="relative z-10 flex justify-center gap-2 py-2 bg-black/30">
          {holeCards.map((card, i) => (
            <PokerCard
              key={`hole-${card.suit}_${card.value}`}
              card={card}
              size="md"
              delay={i * 0.15}
              flipDelayMs={holeCardsJustDealt ? 600 + i * 200 : 0}
            />
          ))}
        </div>
      )}

      {/* Action Bar */}
      <AnimatePresence>
        {isMyTurn && roomState?.phase !== "waiting" && roomState?.phase !== "showdown" && (
          <motion.div
            className="relative z-10 bg-[#1C1C32] border-t border-[#2A2A45] px-4 py-3"
            initial={{ y: 80, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 80, opacity: 0 }}
            transition={{ type: "spring", bounce: 0.2 }}
          >
            <div className="flex flex-col gap-2 max-w-2xl mx-auto">
              {/* Turn timer bar */}
              <div className="w-full h-1.5 bg-white/10 rounded-full overflow-hidden">
                <div
                  className={"h-full rounded-full transition-all duration-1000 linear " + (turnTimeLeft <= 5 ? "bg-red-500" : turnTimeLeft <= 10 ? "bg-yellow-400" : "bg-[#FFD700]")}
                  style={{ width: turnTimerDuration > 0 ? (turnTimeLeft / turnTimerDuration * 100) + "%" : "100%" }}
                />
              </div>
              {/* Fold / Check / Call */}
              <ActionButtons
                canCheck={!!canCheck}
                callAmount={callAmount}
                isMobile={isMobile}
                onFold={() => { playFold(); sendAction("fold") }}
                onCheck={() => { playCheck(); sendAction("check") }}
                onCall={() => { playChipBet(); sendAction("call") }}
              />

              {/* Raise controls + All-In */}
              <div className="flex items-center gap-2">
                <BetControls
                  value={raiseAmount}
                  onChange={setRaiseAmount}
                  min={minRaise}
                  max={myPlayer ? myPlayer.chips + (myPlayer.bet || 0) : 0}
                  step={roomState?.bigBlind || 10}
                  onRaise={() => { playChipsStack(); sendAction("raise", raiseAmount) }}
                  isMobile={isMobile}
                  currentBet={roomState?.currentBet || 0}
                  pot={roomState?.pot || 0}
                />

                {/* All-In */}
                <motion.button
                  onClick={() => { playAllIn(); sendAction("all-in") }}
                  className="px-4 py-2.5 bg-gradient-to-r from-[#FFD700] to-[#FFA500] text-black font-black text-sm border-2 border-[#2A2A45] rounded-xl shadow-[2px_2px_0_0_#1a1a1a,0_0_10px_rgba(255,215,0,0.3)] shrink-0"
                  style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  ALL IN
                </motion.button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Waiting / loading state */}
      {(!roomState || roomState?.phase === "waiting") && (
        <div className="relative z-10 bg-[#1C1C32] border-t border-[#2A2A45] px-4 py-4">
          <div className="flex items-center justify-center gap-3">
          <motion.div
            className="flex items-center justify-center gap-3"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <motion.div
              animate={{ rotate: [0, 360] }}
              transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
            >
              <Spade className="w-5 h-5 text-[#32CD32]" />
            </motion.div>
            <p className="text-white/60 text-sm font-bold" style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}>
              {!roomState
                ? "Connecting to table..."
                : autoStartCountdown != null && roomState.players.length >= 2
                  ? `Game ready! Starting in ${Math.max(0, autoStartCountdown)}s`
                  : `Waiting for players... (${roomState.players.length}/2 minimum)`
              }
            </p>
            <motion.div
              animate={{ rotate: [0, 360] }}
              transition={{ duration: 3, repeat: Infinity, ease: "linear", delay: 1.5 }}
            >
              <Heart className="w-5 h-5 text-red-400" />
            </motion.div>
          </motion.div>
          </div>
          {/* Add Bot Controls */}
          {canAddBot && (
            <div className="flex items-center justify-center gap-2 mt-3 relative">
              <div className="relative">
                <motion.button
                  onClick={() => setShowBotMenu(!showBotMenu)}
                  disabled={addingBot}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-[#9B59B6]/20 text-[#9B59B6] rounded-xl border border-[#9B59B6]/30 text-sm font-bold disabled:opacity-50"
                  style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Bot className="w-4 h-4" />
                  {addingBot ? "Adding..." : "Add Bot"}
                </motion.button>
                <AnimatePresence>
                  {showBotMenu && (
                    <motion.div
                      className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 bg-[#1C1C32] border border-[#2A2A45] rounded-xl overflow-hidden shadow-lg z-30"
                      initial={{ opacity: 0, y: 8, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 8, scale: 0.95 }}
                    >
                      {(["easy", "medium", "hard"] as const).map((diff) => (
                        <button
                          key={diff}
                          onClick={() => handleAddBot(diff)}
                          className="block w-full px-4 py-2 text-sm font-bold text-white/80 hover:bg-white/10 text-left whitespace-nowrap"
                          style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}
                        >
                          {diff === "easy" ? "Easy" : diff === "medium" ? "Medium" : "Hard"}
                          <span className="text-white/40 text-xs ml-2">
                            {diff === "easy" ? "- passive" : diff === "medium" ? "- balanced" : "- aggressive"}
                          </span>
                        </button>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
              {botCount > 0 && (
                <span className="text-white/40 text-xs font-medium" style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}>
                  {botCount} bot{botCount > 1 ? "s" : ""} in room
                </span>
              )}
            </div>
          )}
          {roomState && roomState.players.length < 2 && !canAddBot && (
            <p className="text-white/30 text-xs mt-2 font-medium text-center" style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}>
              Share this room with friends to start playing!
            </p>
          )}
        </div>
      )}
    </div>
  )
}

// ---- Main PokerTab Component ----

export function PokerTab() {
  const { user, getPokerRooms, createPokerRoom, joinPokerRoom, leavePokerRoom } = useAuth()
  const [rooms, setRooms] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [createOpen, setCreateOpen] = useState(false)
  const [activeRoomId, setActiveRoomId] = useState<string | null>(null)
  const [activeRoomName, setActiveRoomName] = useState("")

  const fetchRooms = useCallback(async () => {
    const result = await getPokerRooms()
    if (result.success && result.rooms) {
      setRooms(result.rooms)
    }
    setLoading(false)
  }, [getPokerRooms])

  useEffect(() => {
    fetchRooms()
    const interval = setInterval(fetchRooms, 5000) // Poll every 5s
    return () => clearInterval(interval)
  }, [fetchRooms])

  const handleCreate = async (name: string, settings: any) => {
    const result = await createPokerRoom(name, settings)
    if (result.success && result.room) {
      setActiveRoomId(result.room._id)
      setActiveRoomName(result.room.name)
    }
    fetchRooms()
  }

  const handleJoin = async (roomId: string) => {
    const result = await joinPokerRoom(roomId)
    if (result.success) {
      const room = rooms.find((r) => r._id === roomId)
      setActiveRoomId(roomId)
      setActiveRoomName(room?.name || "Poker Room")
    }
  }

  const handleLeave = async () => {
    if (activeRoomId) {
      await leavePokerRoom(activeRoomId)
      setActiveRoomId(null)
      setActiveRoomName("")
      fetchRooms()
    }
  }

  // If in a room, show the table view
  if (activeRoomId) {
    return (
      <PokerTable
        roomId={activeRoomId}
        roomName={activeRoomName}
        onLeave={handleLeave}
      />
    )
  }

  // Lobby view
  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1a3a2a] via-[#1c3e2e] to-[#142e22] relative overflow-hidden">
      <div className="absolute inset-0">
        <img src={pokerLobbyBg} alt="" aria-hidden="true" className="w-full h-full object-cover" />
      </div>
      <div className="absolute inset-0 bg-gradient-to-b from-[#06120E]/58 via-[#07140F]/35 to-[#07120D]/65" />
      {/* Subtle pattern overlay */}
      <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: "radial-gradient(circle, #fff 1px, transparent 1px)", backgroundSize: "24px 24px" }} />
      {/* Ambient glow */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-emerald-500/10 rounded-full blur-[120px]" />

      <div className="relative p-4 md:p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="flex gap-1">
            <SuitIcon suit="spades" className="w-6 h-6 text-white/60" />
            <SuitIcon suit="hearts" className="w-6 h-6 text-red-400" />
            <SuitIcon suit="diamonds" className="w-6 h-6 text-red-400" />
            <SuitIcon suit="clubs" className="w-6 h-6 text-white/60" />
          </div>
          <h1
            className="text-3xl md:text-4xl font-black text-white"
            style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}
          >
            POKER ROOMS
          </h1>
        </div>
        <motion.button
          onClick={() => setCreateOpen(true)}
          className="flex items-center gap-2 px-4 py-2.5 bg-[#32CD32] text-white font-bold text-sm border-3 border-[#2A2A45] rounded-xl shadow-[0_0_10px_rgba(243,112,32,0.15)]"
          style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}
          whileHover={{ scale: 1.05, boxShadow: "5px 5px 0 0 #1a1a1a" }}
          whileTap={{ scale: 0.95, boxShadow: "2px 2px 0 0 #1a1a1a" }}
        >
          <Plus className="w-4 h-4" />
          Create Room
        </motion.button>
      </div>

      {/* Points display */}
      <div className="mb-5">
        <div className="inline-flex items-center gap-2 px-4 py-2 bg-black/30 border-2 border-white/10 rounded-xl backdrop-blur-sm">
          <span className="text-sm font-bold text-white/50" style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}>
            Your Points:
          </span>
          <span className="text-lg font-black text-[#FFD700]" style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}>
            {(user?.points?.current ?? 0).toLocaleString()}
          </span>
        </div>
      </div>

      {/* Room Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
              className="w-10 h-10 border-4 border-[#FFD700]/20 border-t-[#FFD700] rounded-full mx-auto"
            />
            <p className="text-white/30 text-xs mt-3 font-bold" style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}>Loading rooms...</p>
          </div>
        </div>
      ) : rooms.length === 0 ? (
        <motion.div
          className="flex flex-col items-center justify-center py-10 md:py-16"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          {/* Mini Felt Table Scene */}
          <motion.div
            className="relative w-72 h-44 md:w-96 md:h-56 rounded-[50%] bg-gradient-to-b from-emerald-700 to-emerald-900 border-[5px] border-[#8B6914] shadow-[inset_0_0_40px_rgba(0,0,0,0.3),0_8px_30px_rgba(0,0,0,0.25)]"
            initial={{ scale: 0.8, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            transition={{ type: "spring", bounce: 0.3, delay: 0.1 }}
          >
            {/* Inner felt ring */}
            <div className="absolute inset-3 rounded-[50%] border border-emerald-500/20" />

            {/* Scattered face-down cards (pure CSS) */}
            <motion.div
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
            >
              <div className="flex gap-1.5 md:gap-2">
                {[
                  { rotate: -12, y: 4, delay: 0.5, suit: "spades" },
                  { rotate: -4, y: -2, delay: 0.6, suit: "hearts" },
                  { rotate: 5, y: 2, delay: 0.7, suit: "clubs" },
                  { rotate: 14, y: -3, delay: 0.8, suit: "diamonds" },
                  { rotate: 22, y: 5, delay: 0.9, suit: "spades" },
                ].map((card, i) => (
                  <motion.div
                    key={i}
                    className="w-[38px] h-[54px] md:w-[48px] md:h-[68px] rounded-lg border-2 border-[#2A2A45]/40 shadow-lg bg-gradient-to-br from-[#1a4d2e] to-[#0d3320] flex items-center justify-center relative overflow-hidden"
                    initial={{ opacity: 0, y: -30, rotate: 0 }}
                    animate={{ opacity: 1, y: card.y, rotate: card.rotate }}
                    transition={{ delay: card.delay, type: "spring", bounce: 0.4 }}
                  >
                    {/* Card pattern */}
                    <div className="absolute inset-[3px] rounded-md border border-[#FFD700]/30" />
                    <div className="absolute inset-[6px] rounded-sm border border-[#FFD700]/15" />
                    <SuitIcon suit={card.suit} className="w-4 h-4 md:w-5 md:h-5 text-[#FFD700]/40" />
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Animated suit icons floating around the table */}
            {[
              { suit: "spades", top: "15%", left: "20%", delay: 1.0 },
              { suit: "hearts", top: "18%", left: "75%", delay: 1.2 },
              { suit: "diamonds", top: "65%", left: "15%", delay: 1.4 },
              { suit: "clubs", top: "62%", left: "80%", delay: 1.1 },
            ].map((item, i) => (
              <motion.div
                key={i}
                className="absolute"
                style={{ top: item.top, left: item.left }}
                initial={{ opacity: 0, scale: 0 }}
                animate={{ opacity: 0.25, scale: 1 }}
                transition={{ delay: item.delay, type: "spring", bounce: 0.5 }}
              >
                <motion.div
                  animate={{ y: [0, -4, 0] }}
                  transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut", delay: i * 0.4 }}
                >
                  <SuitIcon suit={item.suit} className="w-5 h-5 md:w-6 md:h-6 text-white/60" />
                </motion.div>
              </motion.div>
            ))}
          </motion.div>

          {/* Text Content */}
          <motion.div
            className="mt-8 text-center max-w-md px-4"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
          >
            <h2
              className="text-2xl md:text-3xl font-black text-white mb-2"
              style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}
            >
              THE TABLE IS EMPTY
            </h2>
            <p
              className="text-white/50 text-sm md:text-base font-medium mb-6"
              style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}
            >
              No active rooms right now. Be the first to deal — create a room and invite your team to play!
            </p>

            <motion.button
              onClick={() => setCreateOpen(true)}
              className="inline-flex items-center gap-2.5 px-6 py-3 bg-[#32CD32] text-white font-bold text-base border-4 border-[#2A2A45] rounded-2xl shadow-[6px_6px_0_0_#1a1a1a]"
              style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif", letterSpacing: "0.5px" }}
              whileHover={{ y: -3, scale: 1.03, boxShadow: "8px 8px 0 0 #1a1a1a" }}
              whileTap={{ scale: 0.97, boxShadow: "2px 2px 0 0 #1a1a1a" }}
            >
              <Plus className="w-5 h-5" />
              CREATE FIRST ROOM
            </motion.button>
          </motion.div>

          {/* Fun chip decorations */}
          <motion.div
            className="flex items-center gap-3 mt-8"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.0 }}
          >
            {[
              { color: "#EF4444", label: "5" },
              { color: "#3B82F6", label: "10" },
              { color: "#32CD32", label: "25" },
              { color: "#F37020", label: "50" },
              { color: "#9B59B6", label: "100" },
            ].map((chip, i) => (
              <motion.div
                key={chip.label}
                className="w-9 h-9 md:w-10 md:h-10 rounded-full border-[3px] border-white/80 flex items-center justify-center shadow-md"
                style={{ backgroundColor: chip.color }}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.1 + i * 0.08 }}
                whileHover={{ y: -4, rotate: 10, scale: 1.15 }}
              >
                <span
                  className="text-white text-[10px] md:text-xs font-black"
                  style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif", textShadow: "0 1px 2px rgba(0,0,0,0.3)" }}
                >
                  {chip.label}
                </span>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {rooms.map((room) => (
            <RoomCard key={room._id} room={room} onJoin={handleJoin} />
          ))}
        </div>
      )}

      {/* Create Room Modal */}
      <AnimatePresence>
        {createOpen && (
          <CreateRoomModal
            open={createOpen}
            onClose={() => setCreateOpen(false)}
            onCreate={handleCreate}
          />
        )}
      </AnimatePresence>
      </div>
    </div>
  )
}
